import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import zxcvbn from 'zxcvbn';
import Layout from '../components/Layout';
import { Card, CardHeader, Button, PageContainer, Input } from '../components/ui';
import { useLanguage } from '../context/LanguageContext';
import { executeBruteForceLab } from '../api/api';

const ATTACK_RATES = {
  onlineRateLimited: 30,
  onlineNoLimit: 3000,
  offlineLeak: 1000000,
};

const OVERLAY_DURATION_MS = {
  SUCCESS: 3200,
  INDETERMINATE: 4200,
  RATE_LIMITED: 5100,
  BLOCKED: 5800,
};
const MAX_DISPLAY_SECONDS = {
  onlineRateLimited: 30 * 24 * 3600,
  onlineNoLimit: 14 * 24 * 3600,
  offlineLeak: 7 * 24 * 3600,
};

function strengthFromScore(score) {
  if (score <= 1) return 'LOW';
  if (score === 2) return 'MEDIUM';
  if (score === 3) return 'HIGH';
  return 'VERY_HIGH';
}

function formatDuration(seconds, t) {
  if (!Number.isFinite(seconds) || seconds <= 0) return t.lessThanSecond;
  if (seconds >= 30 * 24 * 3600) return t.moreThanMonth;
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h`;
  return `${Math.round(seconds / 86400)}d`;
}

function interpolate(template, vars) {
  return Object.entries(vars).reduce(
    (acc, [key, value]) => acc.replace(`{${key}}`, String(value)),
    template
  );
}

function clampSeconds(seconds, scenarioKey) {
  const max = MAX_DISPLAY_SECONDS[scenarioKey] ?? seconds;
  return Math.min(Math.max(seconds, 0), max);
}

function buildHybridEvents(metrics, t) {
  const attemptSummary = interpolate(t.bruteForceEventAttempts, { n: metrics.simulatedAttemptCount });
  const controlSummary = metrics.outcome === 'BLOCKED'
    ? t.bruteForceEventControlBlocked
    : metrics.outcome === 'RATE_LIMITED'
      ? t.bruteForceEventControlRateLimited
      : metrics.outcome === 'SUCCESS'
        ? t.bruteForceEventControlWeak
        : t.bruteForceEventControlPartial;

  const outcomeSummary = metrics.outcome === 'BLOCKED'
    ? t.bruteForceEventOutcomeBlocked
    : metrics.outcome === 'RATE_LIMITED'
      ? t.bruteForceEventOutcomeRateLimited
      : metrics.outcome === 'SUCCESS'
        ? t.bruteForceEventOutcomeCompromised
        : t.bruteForceEventOutcomeIndeterminate;

  return [
    { order: 1, description: t.bruteForceEventRecon, impact: t.bruteForceEventReconImpact },
    { order: 2, description: attemptSummary, impact: t.bruteForceEventAttemptsImpact },
    { order: 3, description: controlSummary, impact: t.bruteForceEventControlImpact },
    { order: 4, description: outcomeSummary, impact: t.bruteForceEventOutcomeImpact },
  ];
}

function buildLabMetrics(password, t) {
  const analysis = zxcvbn(password || '');
  const guesses = Math.max(1, analysis.guesses || 1);
  const entropy = Math.max(0, Math.log2(guesses));
  const strengthLevel = strengthFromScore(analysis.score ?? 0);

  const onlineRateLimitedSeconds = clampSeconds(guesses / ATTACK_RATES.onlineRateLimited, 'onlineRateLimited');
  const onlineNoLimitSeconds = clampSeconds(guesses / ATTACK_RATES.onlineNoLimit, 'onlineNoLimit');
  const offlineLeakSeconds = clampSeconds(guesses / ATTACK_RATES.offlineLeak, 'offlineLeak');

  let outcome = 'INDETERMINATE';
  if (analysis.score >= 4) outcome = 'BLOCKED';
  else if (analysis.score === 3) outcome = 'RATE_LIMITED';
  else if (analysis.score <= 1) outcome = 'SUCCESS';

  const simulatedAttemptCount = outcome === 'SUCCESS'
    ? 4
    : outcome === 'INDETERMINATE'
      ? 8
      : outcome === 'RATE_LIMITED'
        ? 12
        : 24;

  const policySummaryMap = {
    BLOCKED: t.bruteForcePolicyBlocked,
    RATE_LIMITED: t.bruteForcePolicyRateLimited,
    SUCCESS: t.bruteForcePolicySuccess,
    INDETERMINATE: t.bruteForcePolicyIndeterminate,
  };

  return {
    entropyBits: Math.round(entropy * 10) / 10,
    strengthLevel,
    onlineRateLimitedSeconds,
    onlineNoLimitSeconds,
    offlineLeakSeconds,
    estimatedCrackSeconds: Math.round(onlineNoLimitSeconds),
    thresholdBeforeBlock: 6,
    simulatedAttemptCount,
    outcome,
    policySummary: policySummaryMap[outcome],
  };
}

export default function BruteForceLab() {
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [overlayState, setOverlayState] = useState({
    active: false,
    statusKey: 'overlayStatusTrying',
    progress: 0,
    lines: [],
  });
  const terminalRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!terminalRef.current) return;
    terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
  }, [overlayState.lines]);

  const metrics = useMemo(() => buildLabMetrics(password, t), [password, t]);

  const runSimulationOverlay = useCallback(async (currentMetrics) => {
    const durationMs = OVERLAY_DURATION_MS[currentMetrics.outcome] || 4200;
    const shownAttempts = Math.min(16, Math.max(8, currentMetrics.simulatedAttemptCount));
    const intervalMs = Math.max(120, Math.floor((durationMs * 0.65) / shownAttempts));
    const keyEvents = buildHybridEvents(currentMetrics, t);

    const lines = [
      { text: t.bruteForceOverlayInitLine, statusKey: 'overlayStatusTrying' },
      { text: keyEvents[0].description, statusKey: 'overlayStatusTrying' },
    ];
    for (let i = 1; i <= shownAttempts; i += 1) {
      lines.push({
        text: interpolate(t.bruteForceOverlayAttemptLine, { n: i }),
        statusKey: 'overlayStatusTrying',
      });
      if (i === Math.floor(shownAttempts * 0.45) && currentMetrics.outcome !== 'SUCCESS') {
        lines.push({
          text: t.bruteForceOverlayRateLimitLine,
          statusKey: 'overlayStatusRateLimited',
        });
      }
      if (i === Math.floor(shownAttempts * 0.75) && currentMetrics.outcome === 'BLOCKED') {
        lines.push({
          text: t.bruteForceOverlayLockoutLine,
          statusKey: 'overlayStatusBlocked',
        });
      }
    }

    lines.push({ text: keyEvents[1].description, statusKey: 'overlayStatusTrying' });
    lines.push({
      text: keyEvents[2].description,
      statusKey: currentMetrics.outcome === 'BLOCKED' ? 'overlayStatusBlocked' : 'overlayStatusRateLimited',
    });
    lines.push({
      text: keyEvents[3].description,
      statusKey: currentMetrics.outcome === 'SUCCESS' ? 'overlayStatusCompromised' : 'overlayStatusAnalyzing',
    });

    lines.push({ text: t.bruteForceOverlayFinishedLine, statusKey: 'overlayStatusAnalyzing' });

    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    const startAt = Date.now();

    if (!isMountedRef.current) return;
    setOverlayState({
      active: true,
      statusKey: 'overlayStatusTrying',
      progress: 2,
      lines: [t.bruteForceOverlayHeaderLine],
    });

    for (let index = 0; index < lines.length; index += 1) {
      await sleep(intervalMs);
      if (!isMountedRef.current) return;
      const elapsed = Date.now() - startAt;
      const nextProgress = Math.min(96, Math.max(4, Math.round((elapsed / durationMs) * 100)));
      const item = lines[index];
      setOverlayState((prev) => ({
        ...prev,
        statusKey: item.statusKey,
        progress: nextProgress,
        lines: [...prev.lines, item.text],
      }));
    }

    const remainingMs = Math.max(0, durationMs - (Date.now() - startAt));
    if (remainingMs > 0) await sleep(remainingMs);
    if (!isMountedRef.current) return;

    setOverlayState((prev) => ({ ...prev, progress: 100, statusKey: 'overlayStatusAnalyzing' }));
    await sleep(220);
    if (!isMountedRef.current) return;
    setOverlayState({ active: false, statusKey: 'overlayStatusTrying', progress: 0, lines: [] });
    return keyEvents;
  }, [t]);

  const handleSubmit = async () => {
    if (!password || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const events = await runSimulationOverlay(metrics);
      const payload = {
        strengthLevel: metrics.strengthLevel,
        entropyBits: metrics.entropyBits,
        estimatedCrackSeconds: metrics.estimatedCrackSeconds,
        simulatedAttemptCount: metrics.simulatedAttemptCount,
        thresholdBeforeBlock: metrics.thresholdBeforeBlock,
        outcome: metrics.outcome,
        policySummary: metrics.policySummary,
        events: Array.isArray(events) ? events : buildHybridEvents(metrics, t),
      };
      const response = await executeBruteForceLab(organizationId, payload);
      setResult(response);
      navigate(`/org/${organizationId}/simulations`, { state: { justExecutedSimulation: response } });
    } catch (e) {
      setError(e.message || t.errorRunningBruteForceLab);
      setOverlayState({ active: false, statusKey: 'overlayStatusTrying', progress: 0, lines: [] });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <PageContainer>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[#0f172a] dark:text-slate-100 tracking-tight">
            {t.bruteForceLabTitle}
          </h1>
          <p className="text-[#475569] dark:text-slate-400 mt-1">{t.bruteForceLabSubtitle}</p>
        </div>

        <Card className="overflow-hidden mb-6">
          <CardHeader title={t.bruteForceLabHowItWorks} subtitle={t.bruteForceLabHowItWorksSubtitle} />
          <div className="p-6 space-y-4">
            <div className="rounded-lg bg-amber-100 dark:bg-amber-500/15 border border-amber-200 dark:border-amber-500/30 p-4 text-sm text-amber-800 dark:text-amber-300">
              <strong>{t.attention}:</strong> {t.bruteForceLabWarning}
            </div>
            <div>
              <label htmlFor="bf-password" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                {t.bruteForceLabPasswordInputLabel}
              </label>
              <Input
                id="bf-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t.bruteForceLabPasswordInputPlaceholder}
              />
            </div>
            <div className="flex gap-3">
              <Button onClick={handleSubmit} disabled={!password || submitting} loading={submitting}>
                {submitting ? t.runningSimulation : t.startSimulation}
              </Button>
              <Link to={`/org/${organizationId}/simulations`}>
                <Button variant="secondary">{t.back}</Button>
              </Link>
            </div>
            {error && (
              <div className="rounded-lg bg-red-100 dark:bg-red-500/15 border border-red-200 dark:border-red-500/30 px-4 py-3 text-red-800 dark:text-red-400 text-sm">
                {error}
              </div>
            )}
          </div>
        </Card>

        <Card className="overflow-hidden">
          <CardHeader title={t.bruteForceLabEstimatedResult} subtitle={t.bruteForceLabEstimatedResultSubtitle} />
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
              <p className="text-xs text-slate-500 dark:text-slate-400">{t.passwordStrengthLevel}</p>
              <p className="text-base font-semibold text-slate-800 dark:text-slate-100">{t[`strength${metrics.strengthLevel}`]}</p>
            </div>
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
              <p className="text-xs text-slate-500 dark:text-slate-400">{t.passwordEntropyBits}</p>
              <p className="text-base font-semibold text-slate-800 dark:text-slate-100">{metrics.entropyBits}</p>
            </div>
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
              <p className="text-xs text-slate-500 dark:text-slate-400">{t.timeToCrackRateLimited}</p>
              <p className="text-base font-semibold text-slate-800 dark:text-slate-100">{formatDuration(metrics.onlineRateLimitedSeconds, t)}</p>
            </div>
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
              <p className="text-xs text-slate-500 dark:text-slate-400">{t.timeToCrackNoRateLimit}</p>
              <p className="text-base font-semibold text-slate-800 dark:text-slate-100">{formatDuration(metrics.onlineNoLimitSeconds, t)}</p>
            </div>
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
              <p className="text-xs text-slate-500 dark:text-slate-400">{t.timeToCrackOfflineLeak}</p>
              <p className="text-base font-semibold text-slate-800 dark:text-slate-100">{formatDuration(metrics.offlineLeakSeconds, t)}</p>
            </div>
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
              <p className="text-xs text-slate-500 dark:text-slate-400">{t.simulatedOutcome}</p>
              <p className="text-base font-semibold text-slate-800 dark:text-slate-100">{t[`outcome${metrics.outcome}`]}</p>
            </div>
          </div>
        </Card>

        {result && (
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-4">{t.bruteForceLabSavedToHistory}</p>
        )}

        {overlayState.active && (
          <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="w-full max-w-3xl rounded-xl border border-slate-700 bg-slate-900 shadow-2xl overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-700 flex items-center justify-between gap-3">
                <div>
                  <p className="text-slate-100 font-semibold">{t.bruteForceOverlayTitle}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{t.bruteForceOverlaySubtitle}</p>
                </div>
                <span className="inline-flex px-2.5 py-1 rounded-md text-xs font-semibold bg-cyan-500/20 border border-cyan-500/30 text-cyan-300">
                  {t[overlayState.statusKey]}
                </span>
              </div>
              <div className="px-5 py-4">
                <div className="flex items-center justify-between text-xs text-slate-400 mb-2">
                  <span>{t.bruteForceOverlayProgress}</span>
                  <span>{overlayState.progress}%</span>
                </div>
                <div className="h-2 rounded-full bg-slate-700 overflow-hidden mb-4">
                  <div className="h-full bg-cyan-400 transition-all duration-150" style={{ width: `${overlayState.progress}%` }} />
                </div>
                <div
                  ref={terminalRef}
                  className="h-56 overflow-y-auto rounded-lg border border-slate-700 bg-slate-950 p-3 font-mono text-xs text-cyan-200 space-y-1"
                >
                  {overlayState.lines.map((line, index) => (
                    <p key={`${line}-${index}`} className="leading-relaxed">{line}</p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </PageContainer>
    </Layout>
  );
}
